// src/config/chains.js
//
// Single source of truth for every network the app can run on. Everything
// chain-specific (wallet switching, contract addresses, explorer links,
// network labels, native currency symbol, WalletConnect RPCs) is looked up
// from here by chain id — nothing else in the app should hardcode a chain.
//
// ---------------------------------------------------------------------------
// NETWORKS — one block per chain, keyed by a readable name. THIS is where each
// chain's contract addresses live (they are public, so safe to commit — keep
// secrets in .env, not here). When the user picks a network in the header,
// only that network's block is used.
//
// To add a chain: copy a block, change the values, and add its chainId to
// REACT_APP_ENABLED_CHAINS. Nothing else in the app needs touching.
//
//   addresses.eeg / subscribe / usdc / marketplace
//   Empty string = "not deployed on this chain yet" (the selector shows a
//   warning icon and the related actions explain what is missing).
// ---------------------------------------------------------------------------
const NETWORKS = {
  xdc_apothem: {
    chainId: 51,
    hexChainId: "0x33",
    family: "xdc",
    familyLabel: "XDC",
    name: "XDC Apothem",
    shortName: "XDC Apothem",
    isTestnet: true,
    nativeCurrency: { name: "XDC", symbol: "TXDC", decimals: 18 },
    rpcUrl: "https://erpc.apothem.network",
    explorerUrl: "https://testnet.xdcscan.com",
    addresses: {
eeg: "0x34d871dD2ce0B246E6526D3dEB75cb70e3398Cbe",
      subscribe: "0xaBDf4c6f4AD53ddC542F65165BC91Eb72ab6a318",
      usdc: "0xe37b59228A3B6baa17Ddf4108617a74FFE7D2c3b",
      marketplace: "0xF5113958038a2bCCE788bca473Cf3B7727142A25",
    },
  },

  xdc_mainnet: {
    chainId: 50,
    hexChainId: "0x32",
    family: "xdc",
    familyLabel: "XDC",
    name: "XDC Mainnet",
    shortName: "XDC Mainnet",
    isTestnet: false,
    nativeCurrency: { name: "XDC", symbol: "XDC", decimals: 18 },
    rpcUrl: "https://rpc.xinfin.network",
    explorerUrl: "https://xdcscan.com",
    addresses: { eeg: "", subscribe: "", usdc: "", marketplace: "" },
  },

  // Avalanche values are from Avalanche's public network docs — verify before
  // a production release.
  avalanche_fuji: {
    chainId: 43113,
    hexChainId: "0xa869",
    family: "avalanche",
    familyLabel: "Avalanche",
    name: "Avalanche Fuji (C-Chain testnet)",
    shortName: "Avalanche Fuji",
    isTestnet: true,
    nativeCurrency: { name: "Avalanche", symbol: "AVAX", decimals: 18 },
    rpcUrl: "https://api.avax-test.network/ext/bc/C/rpc",
    explorerUrl: "https://testnet.snowtrace.io",
    addresses: {
      // eeg + subscribe: fill in once those contracts are deployed on Fuji.
      eeg: "0x9a2DD2B532edb0E1B482E561ABCefCbc3e3B0e97",
      subscribe: "0x960055C98c3eeFC2cf4c67d574a6D4A07E01B9B5",
      usdc: "0xe37b59228A3B6baa17Ddf4108617a74FFE7D2c3b",
      marketplace: "0xF5113958038a2bCCE788bca473Cf3B7727142A25", 
    },
  },

  avalanche_mainnet: {
    chainId: 43114,
    hexChainId: "0xa86a",
    family: "avalanche",
    familyLabel: "Avalanche",
    name: "Avalanche C-Chain",
    shortName: "Avalanche C-Chain",
    isTestnet: false,
    nativeCurrency: { name: "Avalanche", symbol: "AVAX", decimals: 18 },
    rpcUrl: "https://api.avax.network/ext/bc/C/rpc",
    explorerUrl: "https://snowtrace.io",
    addresses: { eeg: "", subscribe: "", usdc: "", marketplace: "" },
  },

  // ---- Solana. Synthetic chain ids (Solana has none): 900002 mainnet-beta, 900001 devnet.
  // They match backend/config/chains.js and ENABLED_CHAINS: keep all three the same.
  // Not offered until added to REACT_APP_ENABLED_CHAINS. `programs` / token mint
  // addresses fill in as the Solana contracts are wired in (port step 3+). The public
  // RPC below is rate-limited and visible in the browser: for real use point
  // REACT_APP_SOLANA_DEVNET_RPC at a proxy of yours, never at a URL carrying a secret key.
  solana_devnet: {
    chainId: 900001,
    family: "solana",
    familyLabel: "Solana",
    cluster: "devnet",
    name: "Solana Devnet",
    shortName: "Solana Devnet",
    isTestnet: true,
    nativeCurrency: { name: "Solana", symbol: "SOL", decimals: 9 },
    rpcUrl: process.env.REACT_APP_SOLANA_DEVNET_RPC || "https://api.devnet.solana.com",
    explorerUrl: "https://explorer.solana.com",
    explorerQuery: "?cluster=devnet",
    addresses: { usdc: "8Uxq2dkE5s1EnUs5JRMpcMbztRRAirZ7pK2JFgzgCQBm", marketplace: "Gx8bwvpL4EY3Ngxeznp6792MiGZsKoHrsrvUizo7bJxn" },   // marketplace = the marketplace PROGRAM id
  },

  solana_mainnet: {
    chainId: 900002,
    family: "solana",
    familyLabel: "Solana",
    cluster: "mainnet-beta",
    name: "Solana Mainnet",
    shortName: "Solana Mainnet",
    isTestnet: false,
    nativeCurrency: { name: "Solana", symbol: "SOL", decimals: 9 },
    rpcUrl: process.env.REACT_APP_SOLANA_MAINNET_RPC || "https://api.mainnet-beta.solana.com",
    explorerUrl: "https://explorer.solana.com",
    explorerQuery: "",
    addresses: { usdc: "", marketplace: "" },   // marketplace = the marketplace PROGRAM id
  },
};

// chainId -> network block (with its key attached), for lookups by id.
const CHAINS = Object.keys(NETWORKS).reduce((acc, key) => {
  acc[NETWORKS[key].chainId] = { ...NETWORKS[key], key };
  return acc;
}, {});

export const DEFAULT_CHAIN_ID = parseInt(process.env.REACT_APP_DEFAULT_CHAIN_ID || "51", 10);

// Which networks the selector offers / the wallet will accept. Anything in
// CHAINS but not listed here is treated as unsupported.
const ENABLED_CHAIN_IDS = (process.env.REACT_APP_ENABLED_CHAINS || "51,43113")
  .split(",")
  .map((s) => parseInt(s.trim(), 10))
  .filter((id) => CHAINS[id]);

// ------------------------------------------------------------------ lookups

export const toChainId = (v) => {
  if (v === null || v === undefined || v === "") return null;
  const n = typeof v === "string" && v.startsWith("0x") ? parseInt(v, 16) : Number(v);
  return Number.isFinite(n) ? n : null;
};

export const getChain = (chainId) => CHAINS[toChainId(chainId)] || null;
// 'xdc' | 'avalanche' | 'solana' | null. The wallet layer differs per family:
// everything except 'solana' is EVM.
const getFamily = (chainId) => (getChain(chainId) || {}).family || null;
export const isSolanaChain = (chainId) => getFamily(chainId) === "solana";
export const isSupportedChain = (chainId) => ENABLED_CHAIN_IDS.includes(toChainId(chainId));
export const getEnabledChains = () => ENABLED_CHAIN_IDS.map((id) => CHAINS[id]);

// ------------------------------------------------------------ active chain
// The chain the UI is currently "on". It follows the wallet when one is
// connected and is remembered across reloads (so the choice made in the
// selector before connecting is honoured at connect time).

const STORAGE_KEY = "eeap-active-chain";
let activeChainId = (() => {
  try {
    const saved = toChainId(window.localStorage.getItem(STORAGE_KEY));
    if (saved && isSupportedChain(saved)) return saved;
  } catch (e) {}
  return isSupportedChain(DEFAULT_CHAIN_ID) ? DEFAULT_CHAIN_ID : ENABLED_CHAIN_IDS[0];
})();

const listeners = new Set();

export const getActiveChainId = () => activeChainId;
export const getActiveChain = () => CHAINS[activeChainId];

export const setActiveChainId = (chainId) => {
  const id = toChainId(chainId);
  if (!id || !isSupportedChain(id) || id === activeChainId) return;
  activeChainId = id;
  try {
    window.localStorage.setItem(STORAGE_KEY, String(id));
  } catch (e) {}
  listeners.forEach((fn) => {
    try {
      fn(id);
    } catch (e) {}
  });
};

export const subscribeActiveChain = (fn) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};

// ----------------------------------------------------- per-chain helpers
// All of these default to the active chain, so call sites that don't know
// a specific chain just work; call sites that do (e.g. a purchase record
// that stored its own network) pass it explicitly.

const resolve = (chainId) => getChain(chainId) || getActiveChain();

export const getContractAddress = (key, chainId) => resolve(chainId).addresses[key] || "";

export const getNativeSymbol = (chainId) => resolve(chainId).nativeCurrency.symbol;

export const getNetworkLabel = (chainId) => {
  const c = getChain(chainId);
  return c ? c.name : chainId ? `Unsupported network (${chainId})` : "Not connected";
};

const getExplorerBase = (chainId) => resolve(chainId).explorerUrl;
// Solana explorers need the cluster as a query suffix (?cluster=devnet); empty for
// every EVM chain and for Solana mainnet.
const explorerSuffix = (chainId) => resolve(chainId).explorerQuery || "";
export const getExplorerTxUrl = (txHash, chainId) =>
  txHash ? `${getExplorerBase(chainId)}/tx/${txHash}${explorerSuffix(chainId)}` : null;
export const getExplorerAddressUrl = (address, chainId) =>
  address ? `${getExplorerBase(chainId)}/address/${address}${explorerSuffix(chainId)}` : null;

// Trailing-slash "tx" prefix, for code that concatenates `prefix + hash`
// (the old REACT_APP_EXPLORER_URL contract). It cannot carry a suffix, so on
// Solana devnet use getExplorerTxUrl instead.
export const getExplorerTxPrefix = (chainId) => `${getExplorerBase(chainId)}/tx/`;

// EIP-3085 params for wallet_addEthereumChain.
const getAddChainParams = (chainId) => {
  const c = resolve(chainId);
  return {
    chainId: c.hexChainId,
    chainName: c.name,
    nativeCurrency: c.nativeCurrency,
    rpcUrls: [c.rpcUrl],
    blockExplorerUrls: [c.explorerUrl],
  };
};

// Map for WalletConnect's `rpc` option.
// WalletConnect (EIP-155) only knows EVM chains: Solana is left out.
export const getWalletConnectRpcMap = () =>
  ENABLED_CHAIN_IDS.filter((id) => CHAINS[id].family !== "solana").reduce((acc, id) => {
    acc[id] = CHAINS[id].rpcUrl;
    return acc;
  }, {});

// Are the contracts the app needs actually configured on this chain?
const REQUIRED_ADDRESS_KEYS = ["eeg", "subscribe", "usdc", "marketplace"];
// EEG and the subscription contract are EVM contracts; on Solana only the USDC mint
// and the marketplace are needed so far.
const REQUIRED_ADDRESS_KEYS_SOLANA = ["usdc", "marketplace"];
export const getMissingAddresses = (chainId) => {
  const c = resolve(chainId);
  const keys = c.family === "solana" ? REQUIRED_ADDRESS_KEYS_SOLANA : REQUIRED_ADDRESS_KEYS;
  return keys.filter((k) => !c.addresses[k]);
};
export const isChainConfigured = (chainId) => getMissingAddresses(chainId).length === 0;

// Prompts the wallet to switch to `chainId`, adding the network first if the
// wallet doesn't know it yet (error 4902). Works for injected / EIP-1193
// providers (MetaMask, XDCPay, Core); WalletConnect sessions generally can't
// be switched this way, so that path is best-effort and returns false.
export const switchWalletToChain = async (instance, chainId) => {
  const c = getChain(chainId);
  // A Solana wallet cannot be told to switch to an EVM chain (or the reverse): the
  // network selector disconnects and asks for the other wallet instead.
  if (c && c.family === "solana") return false;
  if (!c || !instance || typeof instance.request !== "function") return false;
  try {
    await instance.request({
      method: "wallet_switchEthereumChain",
      params: [{ chainId: c.hexChainId }],
    });
    return true;
  } catch (switchErr) {
    // 4902 = unknown chain. MetaMask mobile sometimes wraps it as -32603.
    const unknownChain =
      switchErr && (switchErr.code === 4902 || (switchErr.data && switchErr.data.originalError && switchErr.data.originalError.code === 4902));
    if (unknownChain) {
      try {
        await instance.request({
          method: "wallet_addEthereumChain",
          params: [getAddChainParams(chainId)],
        });
        return true;
      } catch (addErr) {
        return false;
      }
    }
    return false;
  }
};